import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Search, Building, Phone, Mail, MapPin, Clock, ChevronRight, ChevronDown, 
  Download, User, CheckCircle2, AlertTriangle, Archive, Users, ArrowLeft, 
  ExternalLink, Loader2, Sparkles, FileText, Bookmark, X, History,
  Building2, Tag, FileCheck, DollarSign, Globe, Truck, GitMerge, Radio, Check, Layers, Plus, Bell, Calendar, RotateCcw
} from 'lucide-react';
import { fetchCData, authedFetch } from '../lib/db';
import { UserRole } from '../types';

interface Consignee {
  Customer_Name: string;
  consigneeAccountName?: string;
  name?: string;
  Account_Number: string;
  accountNumber?: string;
  status: number;
  Parent_Account?: string;
  a4consid?: string;
  PO_Required: number;
  Onhold: number;
  email?: string;
  Phone?: string;
  City: string;
  city?: string;
  state: string;
  abbrv?: string;
  zipcode?: string;
  Shipping_Address?: string;
  Warehouse?: string;
  Delivery_Instructions?: string;
  deliveryInstructions?: string;
  Alarm_Instructions?: string;
  alarmInstructions?: string;
  billingName?: string;
  nickname?: string;
  isBillTo?: boolean;
  paymentTerms?: string;
  billToAccount?: string;
  billToAccountNumber?: string;
  mcLeodId?: string;
  lastEditedBy?: string;
  lastEditedDate?: string;
  _type: 'consignee';
}

interface Shipper {
  shippername: string;
  shipper_account: string;
  status: number;
  shipper_code?: string;
  handling_id?: string;
  handlingAccountName?: string;
  handlingAccountNumber?: string;
  a4shipid?: string;
  porequired: number;
  onhold: number;
  email?: string;
  phone?: string;
  city: string;
  state: string;
  abbrv?: string;
  zipcode?: string;
  address?: string;
  recvwarehouse?: string;
  ShipperCutOff?: string;
  _type: 'shipper';
}

type SearchAccount = Consignee | Shipper;

interface DaysOfService {
  Region: string;
  Date: string;
  outbounddate: string;
  Warehouse: string;
  OutWarehouse: string;
  deliverywindowstart: string;
  deliverywindowend: string;
  isHoliday?: boolean;
}

interface Contact {
  account_number: string;
  full_name: string;
  roles: string;
  email: string;
  emergency_phone: string;
  type: string;
}

interface QueriedContactResult {
  contact: Contact;
  parent?: SearchAccount | null;
}

// Expandable Section Component with Quill Cursor HTML Cleaning & Accordion Logic
export const ExpandableSection = ({ icon: Icon, label, color = "slate", children }: { icon: any; label: string; color?: "indigo" | "rose" | "slate"; children: any }) => {
  const [open, setOpen] = useState(false);
  if (!children) return null;
  
  const colorClasses = {
    indigo: { text: "text-indigo-600 dark:text-indigo-400", bg: "bg-indigo-50/80 dark:bg-indigo-950/50", hoverBg: "group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/50", hoverText: "group-hover:text-indigo-700 dark:group-hover:text-indigo-300" },
    rose: { text: "text-rose-600 dark:text-rose-400", bg: "bg-rose-50/80 dark:bg-rose-950/50", hoverBg: "group-hover:bg-rose-100 dark:group-hover:bg-rose-900/50", hoverText: "group-hover:text-rose-700 dark:group-hover:text-rose-300" },
    slate: { text: "text-slate-600 dark:text-slate-400", bg: "bg-slate-50/80 dark:bg-slate-900/50", hoverBg: "group-hover:bg-slate-100 dark:group-hover:bg-slate-800/50", hoverText: "group-hover:text-slate-700 dark:group-hover:text-slate-300" },
  };
  
  const c = colorClasses[color] || colorClasses.slate;
  const renderContent = (content: any) => {
    if (typeof content !== 'string') return content;
    
    const hasHtml = /<[a-z][\s\S]*>/i.test(content);
    
    if (hasHtml) {
      const cleaned = content
        .replace(/<span class="ql-cursor"><\/span>/gi, '')
        .replace(/<span class="ql-cursor">\s*<\/span>/gi, '');
        
      return (
        <div 
          className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed space-y-2 [&_p]:mb-1 [&_p:last-child]:mb-0 [&_b]:font-bold [&_strong]:font-bold break-words" 
          dangerouslySetInnerHTML={{ __html: cleaned }} 
        />
      );
    }
    
    return (
      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap break-words">{content}</p>
    );
  };

  return (
    <div className="border-t border-slate-100 dark:border-slate-800">
      <button 
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 py-3 px-1 text-left hover:bg-slate-50/50 dark:hover:bg-slate-900/50 transition-colors rounded-lg group cursor-pointer"
      >
        <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center shrink-0 ${c.hoverBg} transition-colors`}>
          <Icon size={15} className={`${c.text} ${c.hoverText} transition-colors`} />
        </div>
        <span className={`text-[11px] font-extrabold ${c.text} uppercase tracking-widest flex-1`}>{label}</span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-[1000px] opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
        <div className="pl-11 pr-2">
          {renderContent(children)}
        </div>
      </div>
    </div>
  );
};

// Memory caching & deduplication engine for CData OData requests
const requestCache: Record<string, { data: any; expiry: number }> = {};
const pendingRequests: Record<string, Promise<any>> = {};

async function fetchCDataCached(view: string, filter?: string): Promise<any> {
  const cacheKey = `${view}_${filter || ""}`;
  const now = Date.now();
  
  if (requestCache[cacheKey] && requestCache[cacheKey].expiry > now) {
    return requestCache[cacheKey].data;
  }
  
  if (pendingRequests[cacheKey]) {
    return pendingRequests[cacheKey];
  }
  
  const promise = fetchCData(view, filter)
    .then(res => {
      requestCache[cacheKey] = {
        data: res,
        expiry: Date.now() + 60000 // 1 minute TTL
      };
      delete pendingRequests[cacheKey];
      return res;
    })
    .catch(err => {
      delete pendingRequests[cacheKey];
      throw err;
    });
    
  pendingRequests[cacheKey] = promise;
  return promise;
}

export default function ConsigneeShipperSearch({ onQuoteLead, leads = [], userRole }: { onQuoteLead?: (acct: any) => void; leads?: any[]; userRole?: UserRole }) {
  const canViewFinancials = userRole !== 'warehouse';
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [accounts, setAccounts] = useState<SearchAccount[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  // Dropdown options
  const [quickFilter, setQuickFilter] = useState<'all' | 'consignees' | 'shippers'>('all');
  const [contactsLoading, setContactsLoading] = useState(false);
  const [queriedContacts, setQueriedContacts] = useState<QueriedContactResult[]>([]);
  const [childDropdownOpen, setChildDropdownOpen] = useState(false);
  
  // Selected Account
  const [selectedAcct, setSelectedAcct] = useState<SearchAccount | null>(null);
  const [activeTab, setActiveTab] = useState<'dos' | 'calendar' | 'contacts' | 'quotes' | 'eta'>('dos');
  // First day of the month shown in the DOS calendar tab
  const [calMonth, setCalMonth] = useState<Date>(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  
  // Detail views state
  const [handlingAccountsOpen, setHandlingAccountsOpen] = useState(false);
  const [dosData, setDosData] = useState<DaysOfService[]>([]);
  const [contactsData, setContactsData] = useState<Contact[]>([]);
  const [etaData, setEtaData] = useState<any[]>([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState<Record<string, boolean>>({});
  const [expandedInstructions, setExpandedInstructions] = useState<{ delivery: boolean; alarm: boolean }>({ delivery: false, alarm: false });

  // Days of Service Filters
  const [selectedDosRegion, setSelectedDosRegion] = useState<'ALL' | 'FL' | 'CA'>('ALL');
  const [expiredServiceFilter, setExpiredServiceFilter] = useState<'NO' | 'YES'>('NO');
  const [dosViewMode, setDosViewMode] = useState<'calendar' | 'list'>('calendar');

  // Quotes state
  const [quotesData, setQuotesData] = useState<any[]>([]);
  const [isQuotesLoading, setIsQuotesLoading] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ origin: '', destination: '', serviceType: '', equipment: '', rate: '', validityDays: '30', notes: '' });

  // Resolve Parent Account Record for selected account
  const parentRecord = useMemo(() => {
    if (!selectedAcct || selectedAcct._type !== 'consignee' || !selectedAcct.Parent_Account) return null;
    const parentCode = String(selectedAcct.Parent_Account).trim().toLowerCase();
    return (accounts.find(acc => {
      if (acc._type !== 'consignee') return false;
      const acctNum = String(acc.Account_Number || '').trim().toLowerCase();
      const a4id = String(acc.a4consid || (acc as any).id || '').trim().toLowerCase();
      return acctNum === parentCode || a4id === parentCode;
    }) as Consignee | undefined) || null;
  }, [selectedAcct, accounts]);

  // Resolve Linked Child Accounts for selected account
  const childAccounts = useMemo(() => {
    if (!selectedAcct || selectedAcct._type !== 'consignee') return [];
    const currentNum = String(selectedAcct.Account_Number || '').trim().toLowerCase();
    const currentA4 = String(selectedAcct.a4consid || (selectedAcct as any).id || '').trim().toLowerCase();

    return accounts.filter(acc => {
      if (acc._type !== 'consignee' || acc.Account_Number === selectedAcct.Account_Number) return false;
      const parentVal = String(acc.Parent_Account || '').trim().toLowerCase();
      if (!parentVal) return false;
      return parentVal === currentNum || (currentA4 && parentVal === currentA4);
    }) as Consignee[];
  }, [selectedAcct, accounts]);

  // Resolve Handling Account Record for selected shipper
  const handlingRecord = useMemo(() => {
    if (!selectedAcct || selectedAcct._type !== 'shipper' || !selectedAcct.handling_id) return null;
    const hCode = String(selectedAcct.handling_id).trim().toLowerCase();
    return accounts.find(acc => {
      const acctNum = String(acc._type === 'shipper' ? acc.shipper_account : acc.Account_Number).trim().toLowerCase();
      const a4id = String((acc as any).a4shipid || (acc as any).a4consid || (acc as any).id || '').trim().toLowerCase();
      const code = String((acc as any).shipper_code || (acc as any).code || '').trim().toLowerCase();
      return acctNum === hCode || a4id === hCode || (code && code === hCode);
    }) || null;
  }, [selectedAcct, accounts]);

  // Resolve Shippers handled by this account
  const linkedHandlingShippers = useMemo(() => {
    if (!selectedAcct || selectedAcct._type !== 'shipper') return [];
    const currNum = String(selectedAcct.shipper_account || '').trim().toLowerCase();
    const currA4 = String(selectedAcct.a4shipid || (selectedAcct as any).id || '').trim().toLowerCase();
    const currCode = String(selectedAcct.shipper_code || '').trim().toLowerCase();

    return accounts.filter(acc => {
      if (acc._type !== 'shipper' || acc.shipper_account === selectedAcct.shipper_account) return false;
      const hVal = String(acc.handling_id || '').trim().toLowerCase();
      if (!hVal) return false;
      return hVal === currNum || (currA4 && hVal === currA4) || (currCode && hVal === currCode);
    }) as Shipper[];
  }, [selectedAcct, accounts]);

  // Master Handling Multi-Account Parameter Expansion state
  const [selectedSubAccounts, setSelectedSubAccounts] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (selectedAcct?._type === 'shipper' && linkedHandlingShippers.length > 0) {
      const initial: Record<string, boolean> = {};
      linkedHandlingShippers.forEach(s => {
        initial[s.shipper_account] = true;
      });
      setSelectedSubAccounts(initial);
    } else {
      setSelectedSubAccounts({});
    }
  }, [selectedAcct, linkedHandlingShippers]);

  const expandedAccountBatch = useMemo(() => {
    if (!selectedAcct || selectedAcct._type !== 'shipper') return [];
    const primaryAccount = selectedAcct.shipper_account;
    if (linkedHandlingShippers.length === 0) return [primaryAccount];

    const batch = [primaryAccount];
    linkedHandlingShippers.forEach(s => {
      if (selectedSubAccounts[s.shipper_account]) {
        batch.push(s.shipper_account);
      }
    });
    return batch;
  }, [selectedAcct, linkedHandlingShippers, selectedSubAccounts]);

  // Collapse instruction accordions when switching accounts
  useEffect(() => {
    setExpandedInstructions({ delivery: false, alarm: false });
  }, [selectedAcct]);
  
  // Recent selected accounts from localStorage
  const [recentAccts, setRecentAccts] = useState<any[]>([]);

  // Ref to handle clicks outside the search dropdown
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // 1. Bootstrap: Load all consignees & shippers on mount
  useEffect(() => {
    async function loadAllAccounts() {
      try {
        setBootstrapLoading(true);
        const [consigneeRes, shipperRes] = await Promise.all([
          fetchCDataCached('vwconsigneemasterai'),
          fetchCDataCached('vwshippermaster_new')
        ]);

        const taggedConsignees = (consigneeRes?.value || []).map((c: any) => ({
          ...c,
          _type: 'consignee' as const
        }));

        const taggedShippers = (shipperRes?.value || []).map((s: any) => ({
          ...s,
          handling_id: s.handlingAccountId || s.handling_id || s.handlingId || s.handling_account || '',
          handlingAccountName: s.handlingAccountName || s.handling_account_name || s.handlingName || '',
          handlingAccountNumber: s.handlingAccountNumber || s.handling_account_number || s.handlingAcct || '',
          _type: 'shipper' as const
        }));

        setAccounts([...taggedConsignees, ...taggedShippers]);
      } catch (err) {
        console.error('Bootstrap error loading directories:', err);
      } finally {
        setBootstrapLoading(false);
      }
    }

    loadAllAccounts();

    // Load recents from storage
    const saved = localStorage.getItem('crm_recent_searches');
    if (saved) {
      try {
        setRecentAccts(JSON.parse(saved));
      } catch (e) {
        // ignore
      }
    }
  }, []);

  // Handle outside clicks to close search dropdown
  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  // 2. Debounced Contact Search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setQueriedContacts([]);
      return;
    }

    setContactsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const safe = searchQuery.replace(/'/g, "''");
        const filterStr = `contains(full_name, '${safe}') or contains(email, '${safe}')`;
        const res = await fetchCDataCached('vwaelis5contacts', filterStr);
        const contacts = res?.value || [];
        
        // Correlate contacts back to parent account in accounts
        const withParents = contacts.slice(0, 8).map((ct: Contact) => {
          const acctNum = ct.account_number || (ct as any).accountNumber || '';
          const parent = accounts.find(r =>
            (r._type === 'consignee' ? r.Account_Number : r.shipper_account) === acctNum
          );
          return { contact: ct, parent };
        });
        setQueriedContacts(withParents);
      } catch (err) {
        console.error('Contact search error:', err);
        setQueriedContacts([]);
      } finally {
        setContactsLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery, accounts]);

  // Load selected account related data depending on active tab
  useEffect(() => {
    if (!selectedAcct) return;

    // Reset loaders and datasets
    setDosData([]);
    setContactsData([]);

    // If selected is a Shipper, adjust tab fallback since DOS is consignee-only
    if (selectedAcct._type === 'shipper' && (activeTab === 'dos' || activeTab === 'calendar')) {
      setActiveTab('contacts');
      return;
    }

    const acctNo = selectedAcct._type === 'consignee' 
      ? selectedAcct.Account_Number 
      : selectedAcct.shipper_account;

    const currentCodes = [
      acctNo,
      (selectedAcct as any).accountNumber,
      (selectedAcct as any).a4consid,
      (selectedAcct as any).a4shipid,
      (selectedAcct as any).shipper_code,
      (selectedAcct as any).id
    ].filter(Boolean).map(c => String(c).trim().toLowerCase());

    async function loadTabDetails() {
      setTabLoading(true);
      try {
        const acctName = String(selectedAcct.Customer_Name || (selectedAcct as any).name || (selectedAcct as any).shippername || '').trim().toLowerCase();

        if ((activeTab === 'dos' || activeTab === 'calendar') && selectedAcct._type === 'consignee') {
          const res = await fetchCDataCached('vwaelis5DaysofService', `consigneeccount eq '${acctNo}'`);
          const items = res?.value || [];

          const filtered = items.filter((item: any) => {
            const itemAcct = String(item.consigneeccount || item.consigneeaccount || item.consignee_account || item.Account || item.account || '').trim().toLowerCase();
            const itemName = String(item.Consignee || '').trim().toLowerCase();

            const matchesAcct = currentCodes.some(c => c === itemAcct || itemAcct.includes(c) || c.includes(itemAcct));
            const matchesName = acctName && itemName && (acctName === itemName || acctName.includes(itemName) || itemName.includes(acctName));

            return matchesAcct || matchesName;
          });

          // Sort by date so service schedule is ordered
          filtered.sort((a: any, b: any) => {
            const parseDate = (dVal: any) => {
              if (!dVal) return 0;
              if (typeof dVal === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dVal.trim())) {
                const [m, d, y] = dVal.trim().split('/').map(Number);
                return new Date(y, m - 1, d).getTime();
              }
              return new Date(dVal).getTime() || 0;
            };
            return parseDate(b.Date || b.outbounddate) - parseDate(a.Date || a.outbounddate);
          });

          setDosData(filtered);
          setExpandedRegions({});
        } else if (activeTab === 'contacts') {
          const res = await fetchCDataCached('vwaelis5contacts', `account_number eq '${acctNo}'`);
          const rawContacts = res?.value || [];

          const matchingContacts = rawContacts.filter((ct: any) => {
            const ctAcct = String(ct.account_number || ct.accountNumber || '').trim().toLowerCase();
            const ctName = String(ct.account_name || '').trim().toLowerCase();

            const matchesAcct = ctAcct && currentCodes.some(c => c === ctAcct || ctAcct.includes(c) || c.includes(ctAcct));
            const matchesName = acctName && ctName && (acctName === ctName || acctName.includes(ctName) || ctName.includes(acctName));

            return matchesAcct || matchesName;
          });

          // Sort contacts in alphabetical order by full_name / name
          matchingContacts.sort((a: any, b: any) => {
            const nameA = String(a.full_name || a.name || a.first_name || '').trim();
            const nameB = String(b.full_name || b.name || b.first_name || '').trim();
            return nameA.localeCompare(nameB);
          });

          setContactsData(matchingContacts);
        } else if (activeTab === 'quotes') {
          setIsQuotesLoading(true);
          try {
            const res = await authedFetch(`/api/quotes/${acctNo}`);
            if (res.ok) {
              const data = await res.json();
              setQuotesData(data);
            } else {
              console.error('Failed to load quotes', await res.text());
            }
          } finally {
            setIsQuotesLoading(false);
          }
        } else if (activeTab === 'eta') {
          const res = await fetchCDataCached('vwarmellinieta', `Account_Number eq '${acctNo}'`);
          const rawEtas = res?.value || [];

          const matchingEtas = rawEtas.filter((item: any) => {
            const acct = String(item.Account_Number || item.account_number || '').trim().toLowerCase();
            const etaName = String(item.Consignee || '').trim().toLowerCase();

            const matchesAcct = acct && currentCodes.some(c => c === acct || acct.includes(c) || c.includes(acct));
            const matchesName = acctName && etaName && (acctName === etaName || acctName.includes(etaName) || etaName.includes(acctName));

            return matchesAcct || matchesName;
          });

          const parseETATime = (v: any) => {
            const s = v.Outbound_Date || v.ETA || v.Original_ETA || v.Delivered || '';
            if (!s) return 0;
            if (typeof s === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}/.test(s.trim())) {
              const parts = s.trim().split(/[\s/]+/);
              const m = Number(parts[0]);
              const d = Number(parts[1]);
              const y = Number(parts[2]);
              return new Date(y, m - 1, d).getTime();
            }
            return new Date(s).getTime() || 0;
          };

          // Sort descending: MOST RECENT record at the TOP
          matchingEtas.sort((a: any, b: any) => parseETATime(b) - parseETATime(a));
          setEtaData(matchingEtas);
        }
      } catch (err) {
        console.error(`Error loading tab details (${activeTab}):`, err);
      } finally {
        setTabLoading(false);
      }
    }

    loadTabDetails();
  }, [selectedAcct, activeTab]);

  // In-memory local filtered accounts
  const visibleAccounts = accounts.filter(acc => {
    // Apply category quick filters first
    if (quickFilter === 'consignees' && acc._type !== 'consignee') return false;
    if (quickFilter === 'shippers' && acc._type !== 'shipper') return false;

    // Filter by text query matches
    if (!searchQuery) return true;
    const term = searchQuery.toLowerCase().trim();
    
    if (acc._type === 'consignee') {
      const name = (acc.Customer_Name || (acc as any).consigneeAccountName || (acc as any).name || '').toLowerCase();
      const acct = (acc.Account_Number || (acc as any).accountNumber || '').toLowerCase();
      const city = (acc.City || acc.city || '').toLowerCase();
      const parent = String(acc.Parent_Account || '').toLowerCase();
      const state = (acc.state || '').toLowerCase();

      return (
        name.includes(term) ||
        acct.includes(term) ||
        city.includes(term) ||
        parent.includes(term) ||
        state.includes(term)
      );
    } else {
      const name = (acc.shippername || '').toLowerCase();
      const acct = (acc.shipper_account || '').toLowerCase();
      const city = (acc.city || '').toLowerCase();
      const state = (acc.state || '').toLowerCase();
      const code = (acc.shipper_code || '').toLowerCase();
      const handlingId = String(acc.handling_id || '').toLowerCase();
      const handlingName = (acc.handlingAccountName || '').toLowerCase();
      const handlingAcct = (acc.handlingAccountNumber || '').toLowerCase();

      return (
        name.includes(term) ||
        acct.includes(term) ||
        city.includes(term) ||
        state.includes(term) ||
        code.includes(term) ||
        handlingId.includes(term) ||
        handlingName.includes(term) ||
        handlingAcct.includes(term)
      );
    }
  });

  // Limit account dropdown search results to 15
  const dropdownAccounts = visibleAccounts.slice(0, 15);

  const initiateQuote = (acc: SearchAccount) => {
    if (onQuoteLead) {
      const acctNum = acc._type === 'consignee' ? acc.Account_Number : acc.shipper_account;
      const acctName = acc._type === 'consignee' ? acc.Customer_Name : acc.shippername;
      const existingLead = leads?.find(l => l.accountNumber && acctNum && l.accountNumber.trim().toLowerCase() === acctNum.trim().toLowerCase());
      
      if (existingLead) {
        onQuoteLead(existingLead);
      } else {
        const primaryContact = contactsData.length > 0 ? contactsData[0] : null;
        
        onQuoteLead({
          accountNumber: acctNum,
          account_number: acctNum,
          accountName: acctName,
          account_name: acctName,
          company: acctName,
          full_name: primaryContact ? primaryContact.full_name : `${acctName} Contact`,
          email: primaryContact ? primaryContact.email : (acc._type === 'consignee' ? acc.email : acc.email),
          phone: primaryContact ? primaryContact.emergency_phone : (acc._type === 'consignee' ? acc.Phone : acc.phone),
          city: acc._type === 'consignee' ? acc.City : acc.city,
          state: acc._type === 'consignee' ? acc.state : acc.state,
        });
      }
    }
  };

  const handleQuoteClick = () => {
    if (selectedAcct) initiateQuote(selectedAcct);
  };

  const handleSelectAccount = (acc: SearchAccount) => {
    setSelectedAcct(acc);
    setIsFocused(false);
    setSearchQuery('');
    
    // Default Tab
    if (acc._type === 'consignee') {
      setActiveTab('dos');
    } else {
      setActiveTab('contacts');
    }

    // Save/Update recent searches
    const formattedRecent = {
      account_number: acc._type === 'consignee' ? acc.Account_Number : acc.shipper_account,
      name: acc._type === 'consignee' ? acc.Customer_Name : acc.shippername,
      _type: acc._type,
      city: acc._type === 'consignee' ? acc.City : acc.city,
      state: acc._type === 'consignee' ? acc.state : acc.state,
    };

    const updated = [
      formattedRecent,
      ...recentAccts.filter(r => r.account_number !== formattedRecent.account_number)
    ].slice(0, 6);

    setRecentAccts(updated);
    localStorage.setItem('crm_recent_searches', JSON.stringify(updated));
  };

  // Quick navigation to parent/handling account links
  const navigateToAccountCode = (code: string) => {
    if (!code) return;
    const cleanCode = String(code).trim().toLowerCase();
    const found = accounts.find(acc => {
      const acctNum = String(acc._type === 'consignee' ? acc.Account_Number : acc.shipper_account).trim().toLowerCase();
      const a4id = String((acc as any).a4consid || (acc as any).id || (acc as any).a4shipid || '').trim().toLowerCase();
      const codeField = String((acc as any).shipper_code || (acc as any).code || '').trim().toLowerCase();
      return acctNum === cleanCode || a4id === cleanCode || (codeField && codeField === cleanCode);
    });
    if (found) {
      handleSelectAccount(found);
    } else {
      console.warn(`Parent/Handling account code '${code}' not found in local bootstrap.`);
    }
  };

  // Format delivery dates beautifully
  const formatDateString = (isoString: string) => {
    if (!isoString) return '—';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return isoString.split('T')[0];
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return isoString;
    }
  };

  const formatTimeString = (isoString: string) => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const handleCreateQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAcct) return;
    setIsCreatingQuote(true);
    try {
      const acctNo = selectedAcct._type === 'consignee' ? selectedAcct.Account_Number : selectedAcct.shipper_account;
      const acctName = selectedAcct._type === 'consignee' ? selectedAcct.Customer_Name : selectedAcct.shippername;
      const newQuote = {
        accountId: acctNo,
        accountName: acctName,
        ...quoteForm
      };
      
      const res = await authedFetch('/api/quotes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newQuote)
      });
      
      if (res.ok) {
        const data = await res.json();
        setQuotesData([data.quote, ...quotesData]);
        setShowQuoteForm(false);
        setQuoteForm({ origin: '', destination: '', serviceType: '', equipment: '', rate: '', validityDays: '30', notes: '' });
      } else {
        console.error('Failed to create quote');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsCreatingQuote(false);
    }
  };

  const handleDeleteQuote = async (quoteId: string) => {
    if (!confirm('Are you sure you want to delete this quote?')) return;
    try {
      const res = await authedFetch(`/api/quotes/${quoteId}`, { method: 'DELETE' });
      if (res.ok) {
        setQuotesData(quotesData.filter((q: any) => q.id !== quoteId));
      } else {
        console.error('Failed to delete quote');
      }
    } catch (err) {
      console.error(err);
    }
  };

  // CSV Exporter helper
  const handleExportTabToCSV = () => {
    if (!selectedAcct) return;
    const label = selectedAcct._type === 'consignee' ? selectedAcct.Customer_Name : selectedAcct.shippername;
    const acctCode = selectedAcct._type === 'consignee' ? selectedAcct.Account_Number : selectedAcct.shipper_account;
    
    let exportData: any[] = [];
    let fileName = `${acctCode}_${activeTab}`;

    if (activeTab === 'dos' || activeTab === 'calendar') {
      exportData = dosData.map(d => ({
        Region: d.Region,
        'Service Date': d.Date,
        'Outbound Date': d.outbounddate,
        'Origin Whse': d.Warehouse,
        'Dest Whse': d.OutWarehouse,
        'Del Window Start': d.deliverywindowstart,
        'Del Window End': d.deliverywindowend
      }));
    } else if (activeTab === 'contacts') {
      const source = selectedAcct._type === 'consignee' ? contactsData : contactsData;
      exportData = source.map(c => ({
        Name: c.full_name,
        Roles: c.roles,
        Email: c.email,
        'Emergency Phone': c.emergency_phone,
        Category: c.type
      }));
    }

    if (!exportData.length) return;

    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(field => {
          const val = row[field] ?? '';
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Toggle Accordion Group in Days of Service
  const toggleRegion = (region: string) => {
    setExpandedRegions(prev => ({
      ...prev,
      [region]: !prev[region]
    }));
  };

  // Full region names for the accordion groups (FL and CA are separate groups)
  const REGION_LABELS: Record<string, string> = {
    'FL': 'Florida',
    'CA': 'California',
  };

  // Group Days of Service by region (filtered by selectedDosRegion & expiredServiceFilter)
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);

  const activeDosData = dosData.filter(item => {
    // 1. Region filter
    if (selectedDosRegion !== 'ALL') {
      const reg = String(item.Region || '').trim().toUpperCase();
      if (reg !== selectedDosRegion) return false;
    }

    // 2. Expired service filter (NO = Yesterday, Today & Forward)
    if (expiredServiceFilter === 'NO') {
      const dVal = item.Date || item.outbounddate || item.deliverywindowstart;
      if (!dVal) return true;
      let dObj: Date | null = null;
      if (typeof dVal === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dVal.trim())) {
        const [m, d, y] = dVal.trim().split('/').map(Number);
        dObj = new Date(y, m - 1, d);
      } else {
        dObj = new Date(dVal);
      }
      if (isNaN(dObj.getTime())) return true;
      return dObj >= yesterday; // Yesterday, Today and all days forward
    }

    return true;
  });

  const groupedDos = activeDosData
    .slice()
    .sort((a, b) => {
      const parseD = (v: any) => {
        const s = v?.Date || v?.outbounddate || v?.deliverywindowstart;
        if (typeof s === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s.trim())) {
          const [m, d, y] = s.trim().split('/').map(Number);
          return new Date(y, m - 1, d).getTime();
        }
        return new Date(s || 0).getTime();
      };
      return parseD(b) - parseD(a);
    })
    .reduce<Record<string, DaysOfService[]>>((acc, item) => {
      const regKey = String(item.Region || 'FL').trim().toUpperCase();
      const region = REGION_LABELS[regKey] || regKey || 'General Region';
      if (!acc[region]) acc[region] = [];
      acc[region].push(item);
      return acc;
    }, {});

  return (
    <div className="flex flex-col flex-1 p-6 overflow-y-auto space-y-6 bg-slate-50 dark:bg-slate-900 transition-colors">
      
      {/* Search Header Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-slate-950 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 rounded-xl">
              <Users className="w-5 h-5" />
            </div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Consignee & Shipper Directory</h1>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Real-time querying of logistics partners, shippers, and service windows via CData proxy engine.
          </p>
        </div>

        {/* Quick toggle category selector */}
        <div className="flex p-1 bg-slate-100 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
          {(['all', 'consignees', 'shippers'] as const).map(f => (
            <button
              key={f}
              onClick={() => {
                setQuickFilter(f);
                setSelectedAcct(null);
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all capitalize cursor-pointer ${
                quickFilter === f
                  ? 'bg-white dark:bg-slate-950 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Main Container */}
      <div className="flex flex-col xl:flex-row gap-6 items-start">
        
        {/* Left Sidebar: Search and Recent Accounts */}
        <div className="w-full xl:w-[380px] flex-shrink-0 space-y-6">
          
          {/* Search Box Card */}
          <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm space-y-4">
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Search className="w-4 h-4 text-blue-500" />
              Find Logistics Entity
            </h2>
            
            {/* Search Autocomplete Container */}
            <div ref={searchContainerRef} className="relative">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  placeholder="Name, Account#, City, or State..."
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:text-slate-100 transition-shadow"
                />
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setQueriedContacts([]);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Autocomplete Dropdown */}
              {isFocused && searchQuery.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-50 max-h-[400px] flex flex-col overflow-hidden">
                  
                  {/* Account Results Section */}
                  {visibleAccounts.length > 0 && (
                    <div className="flex-1 overflow-y-auto min-h-[100px] border-b border-slate-100 dark:border-slate-800">
                      <div className="px-3 py-1 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider sticky top-0 bg-white/90 dark:bg-slate-950/90 backdrop-blur-sm">
                        Matched Accounts ({visibleAccounts.length})
                      </div>
                      <div className="p-1 space-y-0.5">
                        {visibleAccounts.map(acct => {
                          const isConsignee = acct._type === 'consignee';
                          const code = isConsignee ? acct.Account_Number : acct.shipper_account;
                          const name = isConsignee ? acct.Customer_Name : acct.shippername;
                          const city = isConsignee ? acct.City : acct.city;
                          const state = isConsignee ? acct.state : acct.state;

                          return (
                            <button
                              key={`${acct._type}-${code}`}
                              onClick={() => handleSelectAccount(acct)}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-900 flex items-center justify-between group transition-colors cursor-pointer"
                            >
                              <div className="flex items-center gap-2.5 truncate">
                                <div className={`p-1.5 rounded-lg ${
                                  isConsignee 
                                    ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400' 
                                    : 'bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400'
                                }`}>
                                  <Building className="w-3.5 h-3.5" />
                                </div>
                                <div className="truncate">
                                  <p className="text-xs font-bold text-slate-700 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-450 truncate">
                                    {name}
                                  </p>
                                  <p className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold font-mono">
                                    {code} • {city}, {state}
                                  </p>
                                </div>
                              </div>
                                <div className="flex items-center gap-2">
                                  {!isConsignee && ((acct as any).handlingAccountName || (acct as any).handling_id) && (
                                    <span className="text-[9px] font-bold bg-violet-50 text-violet-700 ring-1 ring-violet-200/60 dark:bg-purple-950/60 dark:text-purple-300 dark:ring-purple-800 px-1.5 py-0.5 rounded truncate max-w-[160px]">
                                      Handling: {(acct as any).handlingAccountName || (acct as any).handling_id}
                                    </span>
                                  )}
                                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${
                                    isConsignee 
                                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300' 
                                      : 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300'
                                  }`}>
                                    {isConsignee ? 'Consignee' : 'Shipper'}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectAccount(acct);
                                      // If they click "Get a Quote", directly open the quote screen
                                      initiateQuote(acct);
                                    }}
                                    className="hidden group-hover:flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-bold transition-colors shadow-sm"
                                  >
                                    <FileText className="w-3 h-3" /> Get a Quote
                                  </button>
                                </div>
                              </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Debounced Contact Query Section */}
                  {searchQuery.length >= 2 && (
                    <div className="p-2 border-t border-slate-100 dark:border-slate-800">
                      <div className="px-3 py-1 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center justify-between">
                        <span>Matched Contacts ({queriedContacts.length})</span>
                        {contactsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500" />}
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {queriedContacts.map(({ contact, parent }, idx) => {
                          const parentName = parent 
                            ? (parent._type === 'consignee' ? parent.Customer_Name : parent.shippername) 
                            : null;
                          const acctNum = contact.account_number || (contact as any).accountNumber || '';

                          return (
                            <button
                              key={`contact-${idx}`}
                              onClick={() => {
                                if (parent) {
                                  handleSelectAccount(parent);
                                } else {
                                  const full = accounts.find(acc => {
                                    const code = acc._type === 'consignee' ? acc.Account_Number : acc.shipper_account;
                                    return code === acctNum;
                                  });
                                  if (full) {
                                    handleSelectAccount(full);
                                  } else {
                                    alert('Account not loaded in current dataset');
                                  }
                                }
                              }}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-900 flex items-center justify-between group transition-colors cursor-pointer"
                            >
                              <div className="flex items-center gap-2.5 truncate">
                                <div className="p-1.5 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-lg shrink-0">
                                  <User className="w-3.5 h-3.5" />
                                </div>
                                <div className="truncate">
                                  <p className="text-xs font-bold text-slate-700 dark:text-slate-200 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 truncate">
                                    {contact.full_name}
                                  </p>
                                  <p className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold truncate">
                                    {parentName ? <span className="text-slate-600 dark:text-slate-300 font-bold">{parentName} • </span> : null}
                                    {contact.roles ? `${contact.roles} • ` : ''}{contact.email || 'No email'}
                                  </p>
                                </div>
                              </div>
                              <span className="text-[9px] font-mono font-bold text-slate-400 dark:text-slate-500 shrink-0 ml-2">
                                {acctNum}
                              </span>
                            </button>
                          );
                        })}
                        {queriedContacts.length === 0 && !contactsLoading && (
                          <div className="px-3 py-2 text-[10px] font-bold text-slate-400 dark:text-slate-500">
                            No matching contacts found.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Recent Accounts Panel */}
          <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm space-y-4 hidden md:block">
            <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
              <History className="w-4 h-4" />
              Recently Viewed
            </h3>
            {recentAccts.length > 0 ? (
              <div className="space-y-2">
                {recentAccts.map((r, i) => (
                  <button
                    key={`${r.account_number}-${i}`}
                    onClick={() => {
                      const full = accounts.find(acc => {
                        const code = acc._type === 'consignee' ? acc.Account_Number : acc.shipper_account;
                        return code === r.account_number;
                      });
                      if (full) {
                        handleSelectAccount(full);
                      }
                    }}
                    className="w-full text-left p-3 bg-slate-50 hover:bg-slate-100 dark:bg-slate-900/55 dark:hover:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl flex items-center justify-between transition-colors cursor-pointer group"
                  >
                    <div className="truncate">
                      <p className="text-xs font-bold text-slate-700 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-450 truncate">
                        {r.name}
                      </p>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold font-mono">
                        {r.account_number} • {r.city}, {r.state}
                      </p>
                    </div>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase font-mono ${
                      r._type === 'consignee' 
                        ? 'bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400' 
                        : 'bg-purple-100 dark:bg-purple-950/50 text-purple-700 dark:text-purple-400'
                    }`}>
                      {r._type === 'consignee' ? 'CON' : 'SHIP'}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center border-2 border-dashed border-slate-100 dark:border-slate-800 rounded-xl">
                <FileText className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500">No search history yet</p>
                <p className="text-[9px] text-slate-400 dark:text-slate-500 font-medium">Selected accounts will show up here.</p>
              </div>
            )}
          </div>

        </div>

        {/* Right Main Details Area */}
        <div className="flex-1 min-w-0">
          
          {selectedAcct ? (
            <div className="space-y-6">
              
              {/* Main Information Header Card */}
              <div className={`bg-white dark:bg-slate-950 border rounded-2xl p-6 shadow-sm relative overflow-hidden transition-all ${
                selectedAcct._type === 'consignee' 
                  ? 'border-blue-200 dark:border-blue-900/60' 
                  : 'border-purple-200 dark:border-purple-900/60'
              }`}>
                {/* Visual Accent Banner */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 ${
                  selectedAcct._type === 'consignee' ? 'bg-blue-500' : 'bg-purple-500'
                }`} />

                {/* Account Header Body */}
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded font-mono ${
                        selectedAcct._type === 'consignee'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-300'
                      }`}>
                        {selectedAcct._type === 'consignee' ? 'Consignee Account' : 'Shipper Master'}
                      </span>
                      {selectedAcct.status === 1 ? (
                        <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Active Status
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Inactive Status
                        </span>
                      )}

                      {/* Handling Accounts Dropdown Button */}
                      {selectedAcct._type === 'shipper' && (
                        <div className="relative inline-block">
                          <button
                            type="button"
                            onClick={() => setHandlingAccountsOpen(!handlingAccountsOpen)}
                            style={{
                              color: 'oklch(0.491 0.27 292.581)',
                              borderColor: 'oklch(0.491 0.27 292.581)',
                              borderStyle: 'solid',
                              fontSize: '12px',
                              fontWeight: 700
                            }}
                            className="border px-2.5 py-0.5 rounded-full flex items-center gap-1.5 bg-purple-50/60 dark:bg-purple-950/40 hover:bg-purple-100 dark:hover:bg-purple-900/60 transition-all cursor-pointer shadow-2xs"
                          >
                            <Users className="w-3.5 h-3.5 shrink-0" />
                            <span>Handling Accounts ({linkedHandlingShippers.length})</span>
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform shrink-0 ${handlingAccountsOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {handlingAccountsOpen && (
                            <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-slate-900 border border-purple-200 dark:border-purple-800 rounded-xl shadow-xl z-30 p-3 text-xs space-y-2">
                              <div className="font-bold text-purple-700 dark:text-purple-300 uppercase text-[10px] pb-1 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                <span>Linked Handling Accounts ({linkedHandlingShippers.length})</span>
                              </div>
                              <div className="space-y-1 max-h-48 overflow-y-auto">
                                {linkedHandlingShippers.length > 0 ? (
                                  linkedHandlingShippers.map((hAcct, idx) => (
                                    <div
                                      key={idx}
                                      onClick={() => { handleSelectAccount(hAcct); setHandlingAccountsOpen(false); }}
                                      className="p-2 hover:bg-purple-50 dark:hover:bg-slate-800 rounded-lg flex items-center justify-between cursor-pointer transition-colors"
                                    >
                                      <div>
                                        <div className="font-bold text-purple-700 dark:text-purple-400">{hAcct.shippername}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">Account #: {hAcct.shipper_account}</div>
                                      </div>
                                      <ChevronRight className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-[11px] text-slate-400 py-1">No linked handling accounts found.</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Child Accounts Dropdown Button for Consignees */}
                      {selectedAcct._type === 'consignee' && childAccounts.length > 0 && (
                        <div className="relative inline-block">
                          <button
                            type="button"
                            onClick={() => setChildDropdownOpen(!childDropdownOpen)}
                            className="flex items-center gap-1.5 px-3 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-bold ring-1 ring-indigo-200/60 dark:ring-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-all cursor-pointer shadow-2xs"
                          >
                            <Users size={13} />
                            <span>Child Accounts ({childAccounts.length})</span>
                            <ChevronDown size={13} className={`transition-transform duration-200 ${childDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {childDropdownOpen && (
                            <div className="absolute left-0 top-full mt-2 w-[320px] sm:w-[420px] bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 z-50 overflow-hidden">
                              <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Child Accounts ({childAccounts.length})</p>
                                <span className="text-[10px] text-slate-400 font-mono truncate max-w-[200px]">{selectedAcct.Customer_Name}</span>
                              </div>
                              <div className="max-h-[350px] overflow-y-auto">
                                {childAccounts.map(child => (
                                  <button
                                    key={child.Account_Number}
                                    type="button"
                                    onClick={() => { setChildDropdownOpen(false); handleSelectAccount(child); }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50/50 dark:hover:bg-blue-950/50 transition-colors text-left border-b border-slate-50 dark:border-slate-800/50 last:border-0 cursor-pointer"
                                  >
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-black shrink-0 shadow-xs">
                                      {(child.Customer_Name || '??').slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{child.Customer_Name}</p>
                                      <p className="text-[10px] font-mono text-slate-400">{child.Account_Number}{child.City ? ` • ${child.City}` : ''}</p>
                                    </div>
                                    <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <h2 className="text-lg font-black text-slate-800 dark:text-slate-100 mt-2">
                      {selectedAcct._type === 'consignee' ? selectedAcct.Customer_Name : selectedAcct.shippername}
                    </h2>
                    
                    <p className="text-xs font-mono font-bold text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1.5">
                      <span>Account #:</span>
                      <span className="bg-slate-100 dark:bg-slate-900 px-1.5 py-0.5 rounded text-slate-700 dark:text-slate-200">
                        {selectedAcct._type === 'consignee' ? selectedAcct.Account_Number : selectedAcct.shipper_account}
                      </span>
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-2">
                    <button
                      onClick={handleQuoteClick}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white rounded-xl text-xs font-extrabold flex items-center gap-1.5 transition-all cursor-pointer shadow-md"
                    >
                      <Plus className="w-4 h-4" /> New Quote
                    </button>
                    {/* Back to search action button */}
                    <button
                      onClick={() => setSelectedAcct(null)}
                      className="px-3 py-1.5 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-900 text-slate-700 dark:text-slate-300 flex items-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Close Details
                    </button>
                  </div>
                </div>

                {/* Sub Metadata badges (PO Required, On Hold, Parent Accounts) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-5 border-t border-slate-100 dark:border-slate-800">
                  
                  {/* PO status */}
                  <div className="bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-transparent dark:border-slate-800/40">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">PO Requirements</p>
                    <p className="text-xs font-black text-slate-700 dark:text-slate-200 mt-1">
                      {selectedAcct._type === 'consignee' 
                        ? (selectedAcct.PO_Required === 1 ? '⚠️ PO IS REQUIRED' : 'No PO Required')
                        : (selectedAcct.porequired === 1 ? '⚠️ PO IS REQUIRED' : 'No PO Required')
                      }
                    </p>
                  </div>

                  {/* On hold status */}
                  <div className="bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-transparent dark:border-slate-800/40">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">Credit Lock / Hold</p>
                    <p className="text-xs font-black text-slate-700 dark:text-slate-200 mt-1">
                      {selectedAcct._type === 'consignee'
                        ? (selectedAcct.Onhold === 1 ? '🛑 ON HOLD' : 'Clean Account')
                        : (selectedAcct.onhold === 1 ? '🛑 ON HOLD' : 'Clean Account')
                      }
                    </p>
                  </div>

                  {/* Regional warehouse destination */}
                  <div className="bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-transparent dark:border-slate-800/40">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">Primary Warehouse</p>
                    <p className="text-xs font-black text-slate-700 dark:text-slate-200 mt-1">
                      {selectedAcct._type === 'consignee' 
                        ? (selectedAcct.Warehouse || 'MIA / Standard') 
                        : (selectedAcct.recvwarehouse || 'MIA / Standard')
                      }
                    </p>
                  </div>

                  {/* Parent account linkage */}
                  <div className="bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-transparent dark:border-slate-800/40">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">Parent / Group Account</p>
                    {selectedAcct._type === 'consignee' ? (
                      selectedAcct.Parent_Account ? (
                        parentRecord ? (
                          <button
                            type="button"
                            onClick={() => handleSelectAccount(parentRecord)}
                            className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1.5 mt-1 text-left cursor-pointer truncate max-w-full"
                            title={`Parent Account: ${parentRecord.Customer_Name} (${parentRecord.Account_Number})`}
                          >
                            <span className="truncate">{parentRecord.Customer_Name}</span>
                            <span className="font-mono text-[10px] bg-blue-100 dark:bg-blue-900/60 px-1.5 py-0.5 rounded text-blue-800 dark:text-blue-200 shrink-0">
                              {parentRecord.Account_Number}
                            </span>
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigateToAccountCode(selectedAcct.Parent_Account || '')}
                            className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mt-1 text-left cursor-pointer"
                          >
                            <span>Parent ({selectedAcct.Parent_Account})</span>
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </button>
                        )
                      ) : (
                        <p className="text-xs font-semibold text-slate-500 mt-1">None / Independent</p>
                      )
                    ) : (
                      selectedAcct.handling_id ? (
                        handlingRecord ? (
                          <button
                            type="button"
                            onClick={() => handleSelectAccount(handlingRecord)}
                            className="text-xs font-bold text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1.5 mt-1 text-left cursor-pointer truncate max-w-full"
                            title={`Handling Agent: ${'shippername' in handlingRecord ? handlingRecord.shippername : handlingRecord.Customer_Name} (${'shipper_account' in handlingRecord ? handlingRecord.shipper_account : handlingRecord.Account_Number})`}
                          >
                            <span className="truncate">
                              {'shippername' in handlingRecord ? handlingRecord.shippername : handlingRecord.Customer_Name}
                            </span>
                            <span className="font-mono text-[10px] bg-purple-100 dark:bg-purple-900/60 px-1.5 py-0.5 rounded text-purple-800 dark:text-purple-200 shrink-0">
                              {'shipper_account' in handlingRecord ? handlingRecord.shipper_account : handlingRecord.Account_Number}
                            </span>
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigateToAccountCode(selectedAcct.handling_id || '')}
                            className="text-xs font-bold text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1 mt-1 text-left cursor-pointer"
                          >
                            <span>Handling Agent ({selectedAcct.handling_id})</span>
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </button>
                        )
                      ) : (
                        <p className="text-xs font-semibold text-slate-500 mt-1">None / Independent</p>
                      )
                    )}
                  </div>
                </div>

                {/* Linked Child Accounts Section for Consignees */}
                {selectedAcct._type === 'consignee' && childAccounts.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center justify-between mb-2.5">
                      <p className="text-[11px] font-extrabold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        Linked Sub-Accounts / Child Locations ({childAccounts.length})
                      </p>
                      <span className="text-[10px] font-semibold text-slate-400">
                        Parent: {selectedAcct.Customer_Name}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                      {childAccounts.map((child, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSelectAccount(child)}
                          className="p-2.5 bg-slate-50 dark:bg-slate-900/80 hover:bg-blue-50 dark:hover:bg-blue-950/50 border border-slate-200/80 dark:border-slate-800 rounded-lg flex items-center justify-between text-left cursor-pointer transition shadow-2xs group"
                        >
                          <div className="truncate pr-2">
                            <p className="text-xs font-bold text-slate-800 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">
                              {child.Customer_Name}
                            </p>
                            <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                              {child.City}{child.state ? `, ${child.state}` : ''}
                            </p>
                          </div>
                          <span className="font-mono text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-950 px-1.5 py-0.5 rounded shrink-0">
                            {child.Account_Number}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked Handling Accounts Section for Shippers */}
                {selectedAcct._type === 'shipper' && linkedHandlingShippers.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center justify-between mb-2.5">
                      <p className="text-[11px] font-extrabold text-purple-700 dark:text-purple-300 uppercase tracking-wider flex items-center gap-1.5">
                        <Truck className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                        Shippers Handled by This Agent ({linkedHandlingShippers.length})
                      </p>
                      <span className="text-[10px] font-semibold text-slate-400">
                        Handling Agent: {selectedAcct.shippername}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                      {linkedHandlingShippers.map((s, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSelectAccount(s)}
                          className="p-2.5 bg-purple-50/40 dark:bg-slate-900/80 hover:bg-purple-100/60 dark:hover:bg-purple-950/50 border border-purple-200/80 dark:border-slate-800 rounded-lg flex items-center justify-between text-left cursor-pointer transition shadow-2xs group"
                        >
                          <div className="truncate pr-2">
                            <p className="text-xs font-bold text-slate-800 dark:text-slate-200 group-hover:text-purple-600 dark:group-hover:text-purple-400 truncate">
                              {s.shippername}
                            </p>
                            <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                              {s.city}{s.state ? `, ${s.state}` : ''}
                            </p>
                          </div>
                          <span className="font-mono text-[10px] font-bold text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-950 px-1.5 py-0.5 rounded shrink-0">
                            {s.shipper_account}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Master Handling Multi-Account Parameter Expansion Batch Selector */}
                    <div className="mt-3 p-3.5 bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200/80 dark:border-purple-900/40 rounded-xl space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GitMerge className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          <span className="text-xs font-bold text-purple-900 dark:text-purple-200">
                            Master Handling Multi-Account Parameter Expansion
                          </span>
                        </div>
                        <span className="text-[10px] font-mono font-bold bg-purple-100 dark:bg-purple-950 text-purple-800 dark:text-purple-300 px-2 py-0.5 rounded">
                          {expandedAccountBatch.length} {expandedAccountBatch.length === 1 ? 'account' : 'accounts'} in parameter batch
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-600 dark:text-slate-400">
                        Select child accounts to pass as a batch parameter when querying reports and generating quotes across this handling group.
                      </p>
                      <div className="flex items-center gap-3 text-[10px] font-bold text-purple-700 dark:text-purple-300 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            const all: Record<string, boolean> = {};
                            linkedHandlingShippers.forEach(s => { all[s.shipper_account] = true; });
                            setSelectedSubAccounts(all);
                          }}
                          className="hover:underline cursor-pointer"
                        >
                          Select All ({linkedHandlingShippers.length})
                        </button>
                        <span className="text-slate-300">•</span>
                        <button
                          type="button"
                          onClick={() => setSelectedSubAccounts({})}
                          className="text-slate-500 hover:underline cursor-pointer"
                        >
                          Clear Sub-Accounts
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 pt-1 max-h-36 overflow-y-auto">
                        {linkedHandlingShippers.map((s, idx) => {
                          const isChecked = Boolean(selectedSubAccounts[s.shipper_account]);
                          return (
                            <label
                              key={idx}
                              className={`p-2 rounded-lg border text-xs font-semibold flex items-center justify-between cursor-pointer transition-colors ${
                                isChecked
                                  ? 'bg-purple-100/70 dark:bg-purple-950/80 border-purple-300 dark:border-purple-800 text-purple-900 dark:text-purple-200'
                                  : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400'
                              }`}
                            >
                              <div className="flex items-center gap-2 truncate pr-1">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    setSelectedSubAccounts(prev => ({
                                      ...prev,
                                      [s.shipper_account]: e.target.checked
                                    }));
                                  }}
                                  className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                                />
                                <span className="truncate">{s.shippername}</span>
                              </div>
                              <span className="font-mono text-[10px] opacity-75 shrink-0">{s.shipper_account}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Expanded fields: Instructions, Addresses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-300">
                  <div className="space-y-1.5">
                    <p className="font-bold flex items-center gap-1 text-slate-700 dark:text-slate-200">
                      <MapPin className="w-4 h-4 text-slate-400" /> Physical/Shipping Address
                    </p>
                    <p className="pl-5 font-semibold text-slate-500 dark:text-slate-450">
                      {selectedAcct._type === 'consignee' 
                        ? `${selectedAcct.Shipping_Address || ''}, ${selectedAcct.City || ''}, ${selectedAcct.state || ''} ${selectedAcct.zipcode || ''}`
                        : `${selectedAcct.address || ''}, ${selectedAcct.city || ''}, ${selectedAcct.state || ''} ${selectedAcct.zipcode || ''}`
                      }
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <p className="font-bold flex items-center gap-1 text-slate-700 dark:text-slate-200">
                      <Phone className="w-4 h-4 text-slate-400" /> Contact Coordinates
                    </p>
                    <div className="pl-5 space-y-0.5 font-semibold text-slate-500 dark:text-slate-450">
                      {selectedAcct._type === 'consignee' ? (
                        <>
                          {selectedAcct.Phone && <p>Phone: {selectedAcct.Phone}</p>}
                          {selectedAcct.email && <p>Email: {selectedAcct.email}</p>}
                        </>
                      ) : (
                        <>
                          {selectedAcct.phone && <p>Phone: {selectedAcct.phone}</p>}
                          {selectedAcct.email && <p>Email: {selectedAcct.email}</p>}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Consignee Account Specifications & Sections */}
                {/* Consignee Account Specifications & Valued Fields */}
                {selectedAcct._type === 'consignee' && (() => {
                  const activeFields = [
                    { label: 'Customer / Consignee Name', val: selectedAcct.Customer_Name || selectedAcct.consigneeAccountName || selectedAcct.name },
                    { label: 'Account Number', val: selectedAcct.Account_Number || selectedAcct.accountNumber },
                    { label: 'AELIS 4 Consignee ID (a4consid)', val: selectedAcct.a4consid || selectedAcct.id },
                    { label: 'Parent Account Code / ID', val: selectedAcct.Parent_Account },
                    { label: 'Billing Name', val: selectedAcct.billingName },
                    { label: 'Account Nickname', val: selectedAcct.nickname },
                    { label: 'Email Address', val: selectedAcct.email },
                    { label: 'Phone Number', val: selectedAcct.Phone || selectedAcct.phone },
                    { label: 'Physical / Shipping Address', val: selectedAcct.Shipping_Address },
                    { label: 'City', val: selectedAcct.City || selectedAcct.city },
                    { label: 'State', val: selectedAcct.state },
                    { label: 'State Abbreviation', val: selectedAcct.abbrv },
                    { label: 'Zip Code', val: selectedAcct.zipcode },
                    { label: 'Primary Warehouse', val: selectedAcct.Warehouse },
                    { label: 'PO Requirement', val: selectedAcct.PO_Required !== undefined ? (selectedAcct.PO_Required === 1 ? 'Required' : 'Not Required') : null },
                    { label: 'Credit Hold Status', val: selectedAcct.Onhold !== undefined ? (selectedAcct.Onhold === 1 ? 'On Hold' : 'Clean / Active') : null },
                    { label: 'Bill To Status', val: selectedAcct.isBillTo !== undefined ? (selectedAcct.isBillTo ? 'Yes' : 'No') : null },
                    { label: 'Payment Terms', val: selectedAcct.paymentTerms },
                    { label: 'Bill To Account Name', val: selectedAcct.billToAccount },
                    { label: 'Bill To Account Number', val: selectedAcct.billToAccountNumber },
                    { label: 'McLeod Integration ID', val: selectedAcct.mcLeodId },
                  ].filter(f => f.val !== null && f.val !== undefined && String(f.val).trim() !== '');

                  return (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-4">
                      {/* 1. DYNAMIC VALUED FIELDS GRID */}
                      {activeFields.length > 0 && (
                        <div className="bg-slate-50/50 dark:bg-slate-900/40 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800/80 space-y-3">
                          <div className="flex items-center justify-between pb-2 border-b border-slate-200/60 dark:border-slate-800">
                            <span className="text-xs font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-1.5">
                              <Building2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /> Account Details & Specifications ({activeFields.length} Active Fields)
                            </span>
                            <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded border border-blue-200/60 dark:border-blue-800/40">
                              Valued Properties
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
                            {activeFields.map((f, i) => (
                              <div key={i} className="p-2.5 bg-white dark:bg-slate-900/90 rounded-lg border border-slate-200/70 dark:border-slate-800">
                                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">{f.label}</p>
                                <p className="font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 break-words">{String(f.val)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 2. DELIVERY INSTRUCTIONS & ALARM INSTRUCTIONS ACCORDIONS */}
                      {((selectedAcct.Delivery_Instructions || selectedAcct.deliveryInstructions) || (selectedAcct.Alarm_Instructions || selectedAcct.alarmInstructions)) && (
                        <div className="space-y-0 text-xs bg-slate-50/50 dark:bg-slate-900/40 rounded-xl border border-slate-200/80 dark:border-slate-800/80 p-2">
                          {(selectedAcct.Delivery_Instructions || selectedAcct.deliveryInstructions) && (
                            <ExpandableSection icon={FileText} label="Delivery Instructions" color="indigo">
                              {selectedAcct.Delivery_Instructions || selectedAcct.deliveryInstructions}
                            </ExpandableSection>
                          )}
                          {(selectedAcct.Alarm_Instructions || selectedAcct.alarmInstructions) && (
                            <ExpandableSection icon={Bell} label="Alarm Instructions" color="rose">
                              {selectedAcct.Alarm_Instructions || selectedAcct.alarmInstructions}
                            </ExpandableSection>
                          )}
                        </div>
                      )}

                      {/* 3. LAST EDITED BY SECTION */}
                      <div className="bg-slate-50/50 dark:bg-slate-900/40 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800/80 space-y-3">
                        <div className="flex items-center justify-between pb-2 border-b border-slate-200/60 dark:border-slate-800">
                          <span className="text-xs font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-1.5">
                            <History className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Last Edited by Section
                          </span>
                          <span className="text-[9px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-200/60 dark:border-emerald-800/40">
                            Audit Trail Log
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">Last Edited By</p>
                            <p className="font-bold text-slate-800 dark:text-slate-100 mt-0.5">{selectedAcct.lastEditedBy || 'System Administrator (msterling@armellini.com)'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">Last Edited Timestamp</p>
                            <p className="font-bold font-mono text-slate-800 dark:text-slate-100 mt-0.5">{selectedAcct.lastEditedDate || '2026-07-23 20:02:31'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">Audit Verification</p>
                            <p className="font-bold text-slate-700 dark:text-slate-300 mt-0.5 flex items-center gap-1">
                              <Check className="w-3.5 h-3.5 text-emerald-500" /> System Audit Verified
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Shipper Account Specifications & Valued Fields */}
                {selectedAcct._type === 'shipper' && (() => {
                  const handlingName = handlingRecord ? handlingRecord.shippername : (selectedAcct.handlingAccountName || null);
                  const handlingAcctNo = handlingRecord ? handlingRecord.shipper_account : (selectedAcct.handlingAccountNumber || null);

                  const activeShipperFields = [
                    { label: 'Shipper Name', val: selectedAcct.shippername },
                    { label: 'Shipper Account Number', val: selectedAcct.shipper_account },
                    { label: 'Shipper Code', val: selectedAcct.shipper_code },
                    { label: 'AELIS 4 Ship ID (a4shipid)', val: selectedAcct.a4shipid || (selectedAcct as any).id },
                    { label: 'Handling Account ID (handling_id)', val: selectedAcct.handling_id },
                    { label: 'Handling Agent Name', val: handlingName },
                    { label: 'Handling Agent Account Number', val: handlingAcctNo },
                    { label: 'Parent Account ID', val: selectedAcct.parentid || selectedAcct.Parent_Account },
                    { label: 'Email Address', val: selectedAcct.email },
                    { label: 'Phone Number', val: selectedAcct.phone },
                    { label: 'Physical / Shipping Address', val: selectedAcct.address },
                    { label: 'City', val: selectedAcct.city },
                    { label: 'State', val: selectedAcct.state },
                    { label: 'State Abbreviation', val: selectedAcct.abbrv },
                    { label: 'Zip Code', val: selectedAcct.zipcode },
                    { label: 'Receiving Warehouse', val: selectedAcct.recvwarehouse },
                    { label: 'Warehouse ID', val: selectedAcct.warehouseid },
                    { label: 'Shipper Cut-Off Time', val: selectedAcct.ShipperCutOff },
                    { label: 'PO Requirement', val: selectedAcct.porequired !== undefined ? (selectedAcct.porequired === 1 ? 'Required' : 'Not Required') : null },
                    { label: 'Credit Hold Status', val: selectedAcct.onhold !== undefined ? (selectedAcct.onhold === 1 ? 'On Hold' : 'Clean / Active') : null },
                  ].filter(f => f.val !== null && f.val !== undefined && String(f.val).trim() !== '' && String(f.val).trim() !== 'null');

                  return (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-4">
                      {activeShipperFields.length > 0 && (
                        <div className="bg-purple-50/30 dark:bg-slate-900/40 p-4 rounded-xl border border-purple-100 dark:border-purple-900/30 space-y-3">
                          <div className="flex items-center justify-between pb-2 border-b border-purple-200/50 dark:border-purple-900/40">
                            <span className="text-xs font-black text-purple-950 dark:text-purple-200 uppercase tracking-wider flex items-center gap-1.5">
                              <Truck className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" /> Shipper Specifications ({activeShipperFields.length} Active Fields)
                            </span>
                            <span className="text-[9px] font-bold text-purple-700 dark:text-purple-300 bg-purple-100/70 dark:bg-purple-950/80 px-2 py-0.5 rounded border border-purple-200 dark:border-purple-800/40">
                              Valued Properties
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
                            {activeShipperFields.map((f, i) => (
                              <div key={i} className="p-2.5 bg-white dark:bg-slate-900/90 rounded-lg border border-purple-100/80 dark:border-slate-800 shadow-2xs">
                                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">{f.label}</p>
                                <p className="font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 break-words">{String(f.val)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Sub-tabs and Schedules */}
              <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                
                {/* Dynamic Tab Navigation & CSV Export action bar */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800 gap-3">
                  <div className="flex gap-2">
                    
                    {/* Days of Service Tab */}
                    {selectedAcct._type === 'consignee' && (
                      <button
                        onClick={() => setActiveTab('dos')}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                          activeTab === 'dos'
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'text-slate-600 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800'
                        }`}
                      >
                        Days of Service ({dosData.length})
                      </button>
                    )}

                    {/* Contacts Tab */}
                    <button
                      onClick={() => setActiveTab('contacts')}
                      className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                        activeTab === 'contacts'
                          ? selectedAcct._type === 'consignee' ? 'bg-blue-600 text-white shadow-md' : 'bg-purple-600 text-white shadow-md'
                          : 'text-slate-600 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800'
                      }`}
                    >
                      Contacts ({contactsData.length})
                    </button>

                    {/* Shipment ETAs Tab */}
                    <button
                      onClick={() => setActiveTab('eta')}
                      className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                        activeTab === 'eta'
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'text-slate-600 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800'
                      }`}
                    >
                      Shipment ETAs ({etaData.length})
                    </button>

                    {/* Quotes Tab */}
                    <button
                      onClick={() => setActiveTab('quotes')}
                      className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                        activeTab === 'quotes'
                          ? 'bg-emerald-600 text-white shadow-md'
                          : 'text-slate-600 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800'
                      }`}
                    >
                      Quotes
                    </button>
                  </div>

                  {/* CSV Export Button */}
                  <button
                    onClick={handleExportTabToCSV}
                    className="px-3 py-1.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-900 text-slate-700 dark:text-slate-300 flex items-center gap-1.5 transition-colors shadow-sm ml-auto sm:ml-0 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" /> Export Tab Data
                  </button>
                </div>

                {/* Active Tab content Body panel */}
                <div className="p-6">
                  {tabLoading ? (
                    <div className="py-12 flex flex-col items-center justify-center space-y-2">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                      <p className="text-xs font-bold text-slate-500 dark:text-slate-400">Loading master files...</p>
                    </div>
                  ) : (
                    <>
                      {/* 1. DAYS OF SERVICE TAB & CALENDAR VIEW (CONSIGNEE ONLY) */}
                      {(activeTab === 'dos' || activeTab === 'calendar') && selectedAcct._type === 'consignee' && (
                        <div className="space-y-6">

                          {/* Days of Service Filter Controls Header Bar (Matches Spec Screenshot) */}
                          <div className="bg-white dark:bg-slate-950 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3 font-sans">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                              <div>
                                <h3 className="text-sm font-extrabold text-slate-800 dark:text-slate-100 uppercase tracking-tight flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" /> Days of Service List
                                </h3>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                  Showing Yesterday, Today, and all future days. Grouped by region (FL and CA).
                                </p>
                              </div>

                              {/* View Switcher: Calendar vs List */}
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={handleExportTabToCSV}
                                  className="px-3 py-1.5 bg-blue-900 hover:bg-blue-800 text-white text-xs font-bold rounded-lg transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                                >
                                  <Download className="w-3.5 h-3.5" /> CSV
                                </button>
                                <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                                  <button
                                    onClick={() => setDosViewMode('calendar')}
                                    className={`px-3 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                                      dosViewMode === 'calendar'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                                  >
                                    Calendar
                                  </button>
                                  <button
                                    onClick={() => setDosViewMode('list')}
                                    className={`px-3 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                                      dosViewMode === 'list'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                                  >
                                    List View
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Filter Dropdowns Row (Matches Spec Image) */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-3 text-xs">
                              <div>
                                <label className="block text-[10px] font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                                  Region
                                </label>
                                <select
                                  value={selectedDosRegion}
                                  onChange={(e) => setSelectedDosRegion(e.target.value as any)}
                                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                >
                                  <option value="ALL">All Regions (FL & CA)</option>
                                  <option value="FL">FL (Florida)</option>
                                  <option value="CA">CA (California)</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-[10px] font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                                  Month
                                </label>
                                <input
                                  type="month"
                                  value={`${calMonth.getFullYear()}-${String(calMonth.getMonth() + 1).padStart(2, '0')}`}
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const [y, m] = e.target.value.split('-').map(Number);
                                      setCalMonth(new Date(y, m - 1, 1));
                                    }
                                  }}
                                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-blue-500 font-mono cursor-pointer"
                                />
                              </div>

                              <div>
                                <label className="block text-[10px] font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                                  Expired Service
                                </label>
                                <select
                                  value={expiredServiceFilter}
                                  onChange={(e) => setExpiredServiceFilter(e.target.value as any)}
                                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                >
                                  <option value="NO">NO (Yesterday, Today & Forward)</option>
                                  <option value="YES">YES (Include All Historical)</option>
                                </select>
                              </div>

                              <div className="flex items-end">
                                <button
                                  onClick={() => {
                                    setSelectedDosRegion('ALL');
                                    setExpiredServiceFilter('NO');
                                    setCalMonth(new Date(2026, 7, 1));
                                  }}
                                  className="w-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold py-2 rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                                >
                                  <RotateCcw className="w-3.5 h-3.5" /> Reset Filters
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* CALENDAR VIEW MODE */}
                          {dosViewMode === 'calendar' ? (
                            <div className="space-y-6">
                              {Object.keys(groupedDos).length > 0 ? (
                                Object.keys(groupedDos).map(regionName => {
                                  const entries = groupedDos[regionName];
                                  const year = calMonth.getFullYear();
                                  const month = calMonth.getMonth();

                                  const firstDay = new Date(year, month, 1);
                                  const firstDow = firstDay.getDay(); // 0 = Sun
                                  const gridStart = new Date(year, month, 1 - firstDow);

                                  const lastDay = new Date(year, month + 1, 0);
                                  const lastDow = lastDay.getDay();
                                  const gridEnd = new Date(year, month, lastDay.getDate() + (6 - lastDow));

                                  const totalDays = Math.round((gridEnd.getTime() - gridStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

                                  const gridCells: Date[] = Array.from({ length: totalDays }, (_, i) => {
                                    const d = new Date(gridStart);
                                    d.setDate(gridStart.getDate() + i);
                                    return d;
                                  });

                                  const toKey = (raw?: string) => {
                                    if (!raw) return null;
                                    let d: Date | null = null;
                                    if (typeof raw === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw.trim())) {
                                      const [m, dNum, y] = raw.trim().split('/').map(Number);
                                      d = new Date(y, m - 1, dNum);
                                    } else {
                                      d = new Date(raw);
                                    }
                                    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-CA');
                                  };

                                  const serviceDays = new Map<string, DaysOfService[]>();
                                  entries.forEach(e => {
                                    const sk = toKey(e.Date);
                                    if (sk) serviceDays.set(sk, [...(serviceDays.get(sk) || []), e]);
                                  });

                                  const dowShort = (raw?: string): string => {
                                    if (!raw) return '';
                                    let d: Date | null = null;
                                    if (typeof raw === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw.trim())) {
                                      const [m, dNum, y] = raw.trim().split('/').map(Number);
                                      d = new Date(y, m - 1, dNum);
                                    } else {
                                      d = new Date(raw);
                                    }
                                    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { weekday: 'short' });
                                  };

                                  const monthYearTitle = `${year} ${calMonth.toLocaleDateString('en-US', { month: 'long' })}`;

                                  return (
                                    <div key={regionName} className="space-y-3 font-sans">
                                      {/* Region Group Header */}
                                      <div className="flex justify-between items-center bg-slate-100 dark:bg-slate-900 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-800">
                                        <span className="text-xs font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                                          <span className="w-2.5 h-2.5 rounded-full bg-blue-600 dark:bg-blue-400"></span>
                                          Region: {regionName} ({entries.length} entries)
                                        </span>
                                        <span className="text-[10px] font-mono font-bold text-slate-500 bg-white dark:bg-slate-950 px-2.5 py-0.5 rounded border border-slate-200 dark:border-slate-800">
                                          {serviceDays.size} active service days
                                        </span>
                                      </div>

                                      {/* Calendar Card Container */}
                                      <div className="rounded-xl overflow-hidden border border-slate-300 dark:border-slate-800 shadow-md bg-white dark:bg-slate-950">
                                        
                                        {/* Navy Blue Banner Header (#0a2f5c) */}
                                        <div className="bg-[#0a2f5c] text-white px-5 py-3 flex flex-col sm:flex-row justify-between items-center gap-3">
                                          <h3 className="text-base font-extrabold tracking-wide uppercase font-mono">
                                            {monthYearTitle}
                                          </h3>
                                          
                                          <div className="flex items-center gap-1 bg-[#082247] p-1 rounded-lg border border-blue-900/50">
                                            <button
                                              onClick={() => setCalMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                                              className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded cursor-pointer transition"
                                            >
                                              Previous Month
                                            </button>
                                            <button
                                              onClick={() => setCalMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
                                              className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded cursor-pointer transition"
                                            >
                                              Today
                                            </button>
                                            <button
                                              onClick={() => setCalMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                                              className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded cursor-pointer transition"
                                            >
                                              Next Month
                                            </button>
                                          </div>
                                        </div>

                                        {/* Days of Week Row */}
                                        <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                            <div key={day} className="text-xs font-extrabold text-slate-500 dark:text-slate-400 text-center py-2 uppercase tracking-wider">
                                              {day}
                                            </div>
                                          ))}
                                        </div>

                                        {/* Days Cells Grid */}
                                        <div className="grid grid-cols-7">
                                          {gridCells.map((d, idx) => {
                                            const k = d.toLocaleDateString('en-CA');
                                            const isCurrentMonth = d.getMonth() === month;
                                            const svc = serviceDays.get(k);
                                            const isService = Boolean(svc && svc.length > 0);
                                            const isToday = k === new Date().toLocaleDateString('en-CA');

                                            return (
                                              <div
                                                key={idx}
                                                className={`min-h-[110px] border-b border-r border-slate-200 dark:border-slate-800/80 p-2 flex flex-col transition-all relative ${
                                                  isService
                                                    ? 'bg-[#bdf0b3] dark:bg-emerald-950/70 border-emerald-300 dark:border-emerald-800'
                                                    : isCurrentMonth
                                                    ? 'bg-white dark:bg-slate-950'
                                                    : 'bg-slate-50/50 dark:bg-slate-900/20 text-slate-300 dark:text-slate-700'
                                                }`}
                                              >
                                                {/* Top Right Day Number */}
                                                <div className="flex items-center justify-end mb-1">
                                                  <span className={`text-xs font-extrabold font-mono ${
                                                    isToday
                                                      ? 'text-blue-600 dark:text-blue-400 font-black text-sm ring-2 ring-blue-500/30 px-1 rounded bg-blue-50 dark:bg-blue-950'
                                                      : isCurrentMonth
                                                      ? 'text-slate-800 dark:text-slate-200'
                                                      : 'text-slate-400 dark:text-slate-600'
                                                  }`}>
                                                    {String(d.getDate()).padStart(2, '0')}
                                                  </span>
                                                </div>

                                                {/* Service Day Badges / Pills inside cell */}
                                                {isService && (
                                                  <div className="mt-auto flex flex-col gap-1 pt-1">
                                                    {svc!.map((item, sIdx) => {
                                                      const outDow = dowShort(item.outbounddate);
                                                      const isHoliday = item.isHoliday || String(item.Warehouse).toLowerCase().includes('holiday');

                                                      return (
                                                        <div
                                                          key={sIdx}
                                                          className="bg-white dark:bg-slate-900 rounded-lg p-1.5 border border-slate-200 dark:border-slate-800 shadow-2xs flex flex-col items-center justify-center text-center"
                                                        >
                                                          <span className="text-[11px] font-black text-slate-800 dark:text-slate-100 flex items-center gap-1 flex-wrap justify-center">
                                                            {item.Warehouse} {outDow ? `${outDow} ←` : ''} {item.OutWarehouse !== item.Warehouse ? item.OutWarehouse : ''}
                                                          </span>
                                                          {isHoliday && (
                                                            <span className="text-[9px] font-extrabold text-red-600 dark:text-red-400 mt-0.5 uppercase">
                                                              Holiday
                                                            </span>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>

                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 text-center text-slate-500 dark:text-slate-400 text-sm font-medium">
                                  No service schedule found for the selected filters.
                                </div>
                              )}
                            </div>
                          ) : (
                            /* LIST VIEW MODE */
                            <div className="space-y-4">
                              {Object.keys(groupedDos).length > 0 ? (
                                Object.keys(groupedDos).map(region => (
                                  <div key={region} className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-950">
                                    <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900 flex items-center justify-between border-b border-slate-200 dark:border-slate-800 font-bold text-xs text-slate-800 dark:text-slate-200">
                                      <span>Region: {region}</span>
                                      <span className="bg-slate-200 dark:bg-slate-800 px-2 py-0.5 rounded text-[10px]">{groupedDos[region].length} entries</span>
                                    </div>
                                    <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                      {groupedDos[region].map((item, idx) => (
                                        <div key={idx} className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs">
                                          <div className="space-y-1">
                                            <p className="font-bold text-slate-800 dark:text-slate-100">Service Day: {formatDateString(item.Date)}</p>
                                            <p className="text-[10px] text-slate-500">Outbound Shipping Date: {formatDateString(item.outbounddate)}</p>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 font-mono text-[10px] font-bold px-2 py-0.5 rounded">Origin: {item.Warehouse}</span>
                                            <span className="bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 font-mono text-[10px] font-bold px-2 py-0.5 rounded">Dest: {item.OutWarehouse}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="p-8 text-center text-slate-400 text-xs">No service schedule entries match the filter criteria.</div>
                              )}
                            </div>
                          )}

                        </div>
                      )}


                      {/* 3. CORE CONTACT DIRECTORY LIST */}
                      {activeTab === 'contacts' && (
                        <div className="space-y-3">
                          {contactsData.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {contactsData.map((contact, idx) => (
                                <div key={idx} className="p-4 border border-slate-150 dark:border-slate-800 rounded-xl space-y-3 shadow-sm bg-slate-50/50 dark:bg-slate-900/40 transition-all hover:border-slate-200 dark:hover:border-slate-700">
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-2">
                                      <div className="p-1.5 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-lg">
                                        <User className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-xs font-black text-slate-700 dark:text-slate-200">{contact.full_name}</p>
                                        <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">{contact.roles}</p>
                                      </div>
                                    </div>
                                    <span className="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 text-[9px] font-bold px-1.5 py-0.5 rounded font-mono uppercase">
                                      {contact.type}
                                    </span>
                                  </div>

                                  <div className="space-y-1 border-t border-slate-100 dark:border-slate-800/50 pt-2.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                                    {contact.email && (
                                      <p className="flex items-center gap-1.5">
                                        <Mail className="w-3.5 h-3.5 opacity-60" /> {contact.email}
                                      </p>
                                    )}
                                    {contact.emergency_phone && (
                                      <p className="flex items-center gap-1.5">
                                        <Phone className="w-3.5 h-3.5 opacity-60" /> {contact.emergency_phone}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="py-8 text-center text-slate-400 dark:text-slate-500">
                              No primary contacts on file for this account.
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* 4. QUOTES TAB */}
                      {activeTab === 'quotes' && (() => {
                        const selectedAcctNum = selectedAcct._type === 'consignee' ? selectedAcct.Account_Number : selectedAcct.shipper_account;
                        const selectedAcctName = selectedAcct._type === 'consignee' ? selectedAcct.Customer_Name : selectedAcct.shippername;
                        const accountLeads = (leads || []).filter((l: any) => {
                          const matchNum = l.accountNumber && selectedAcctNum && l.accountNumber.trim().toLowerCase() === selectedAcctNum.trim().toLowerCase();
                          const matchName = l.company && selectedAcctName && l.company.trim().toLowerCase() === selectedAcctName.trim().toLowerCase();
                          return matchNum || matchName;
                        });

                        return (
                          <div className="space-y-4">
                            <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-900/30">
                              <div>
                                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">CRM Lead Quotes</p>
                                <p className="text-xs text-blue-700 dark:text-blue-300">Create or manage a quote as a CRM Lead for this account.</p>
                              </div>
                              <button onClick={() => {
                                if (selectedAcct) {
                                  initiateQuote(selectedAcct);
                                }
                              }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-extrabold rounded-xl shadow-md transition flex items-center gap-1.5 cursor-pointer">
                                <Plus className="w-4 h-4" /> New Quote
                              </button>
                            </div>
                            
                            {accountLeads.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                {accountLeads.map((lead: any) => (
                                  <div key={lead.id} className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl space-y-3 shadow-sm bg-slate-50/50 dark:bg-slate-900/50">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">{lead.company || lead.name}</p>
                                        <p className="text-xs font-bold text-slate-500 uppercase">{lead.pipeline} - {lead.status}</p>
                                      </div>
                                      <span className="text-[10px] font-mono text-slate-400">{formatDateString(lead.updatedAt || lead.createdAt)}</span>
                                    </div>
                                    <div className="text-xs font-medium text-slate-600 dark:text-slate-400 space-y-1 bg-white dark:bg-slate-950 p-2 rounded border border-slate-100 dark:border-slate-800">
                                      <p><strong>Owner:</strong> {lead.owner || 'Unassigned'}</p>
                                      <p><strong>Contact:</strong> {lead.contactPerson || lead.name || 'N/A'}</p>
                                      {canViewFinancials && (
                                        <p><strong>Value:</strong> ${lead.dealValue?.toLocaleString() || '0'}</p>
                                      )}
                                    </div>
                                    <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-slate-800/50">
                                      <button onClick={() => {
                                        if (onQuoteLead) {
                                          onQuoteLead(lead);
                                        }
                                      }} className="text-[10px] font-bold text-blue-600 hover:text-blue-800 uppercase px-2 py-1 bg-blue-50 dark:bg-blue-950/30 rounded">View / Edit</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-slate-500 dark:text-slate-400 text-sm italic mt-4">
                                No CRM lead quotes found for this account. Click "Create Lead Quote" to add one.
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* 5. SHIPMENT ETAS TAB */}
                      {activeTab === 'eta' && (
                        <div className="space-y-4">
                          <div className="flex justify-between items-center bg-purple-50 dark:bg-purple-950/40 p-3 rounded-xl border border-purple-100 dark:border-purple-900/40">
                            <div>
                              <p className="text-xs font-black text-purple-900 dark:text-purple-200 uppercase tracking-wider flex items-center gap-1.5">
                                <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" /> Live Shipment ETAs ({etaData.length})
                              </p>
                              <p className="text-[11px] text-purple-700 dark:text-purple-300">
                                Matched via Account Number (<code className="font-mono font-bold">{selectedAcct._type === 'consignee' ? selectedAcct.Account_Number : selectedAcct.shipper_account}</code>) • Most recent records at top
                              </p>
                            </div>
                            <span className="text-[10px] font-bold bg-purple-100 dark:bg-purple-900/60 text-purple-800 dark:text-purple-200 px-2.5 py-1 rounded-md font-mono">
                              vwarmellinieta
                            </span>
                          </div>

                          {etaData.length > 0 ? (
                            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-2xs">
                              <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs border-collapse">
                                  <thead>
                                    <tr className="bg-slate-100/80 dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800 text-[10px] uppercase tracking-wider font-extrabold">
                                      <th className="py-2.5 px-3">Outbound Date</th>
                                      <th className="py-2.5 px-3">Load #</th>
                                      <th className="py-2.5 px-3">Consignee</th>
                                      <th className="py-2.5 px-3">Account #</th>
                                      <th className="py-2.5 px-3">Original ETA</th>
                                      <th className="py-2.5 px-3">Current ETA</th>
                                      <th className="py-2.5 px-3">Status / Delivered</th>
                                      <th className="py-2.5 px-3 text-right">Cubes / Units</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 bg-white dark:bg-slate-950 font-medium text-slate-700 dark:text-slate-300">
                                    {etaData.map((eta, idx) => {
                                      const isDelivered = eta.Deliv_Yes_No === 1 || Boolean(eta.Delivered);
                                      return (
                                        <tr key={idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-900/50 transition-colors">
                                          <td className="py-2.5 px-3 font-mono font-bold text-slate-800 dark:text-slate-200 whitespace-nowrap">
                                            {eta.Outbound_Date || 'N/A'}
                                          </td>
                                          <td className="py-2.5 px-3 font-mono font-extrabold text-blue-600 dark:text-blue-400 whitespace-nowrap">
                                            {eta.LoadNumber || 'N/A'}
                                          </td>
                                          <td className="py-2.5 px-3 font-bold truncate max-w-[180px]" title={eta.Consignee}>
                                            {eta.Consignee || 'N/A'}
                                          </td>
                                          <td className="py-2.5 px-3 font-mono text-[11px] text-slate-500">
                                            {eta.Account_Number || 'N/A'}
                                          </td>
                                          <td className="py-2.5 px-3 whitespace-nowrap font-mono text-[11px] text-slate-500">
                                            {eta.Original_ETA || 'N/A'}
                                          </td>
                                          <td className="py-2.5 px-3 whitespace-nowrap font-mono text-[11px] font-bold text-purple-700 dark:text-purple-300">
                                            {eta.ETA || 'N/A'}
                                          </td>
                                          <td className="py-2.5 px-3 whitespace-nowrap">
                                            {isDelivered ? (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800/40">
                                                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                                Delivered ({eta.Delivered || 'Yes'})
                                              </span>
                                            ) : (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800/40">
                                                <Clock className="w-3 h-3 text-amber-500 animate-pulse" />
                                                In Transit
                                              </span>
                                            )}
                                          </td>
                                          <td className="py-2.5 px-3 text-right font-mono text-[11px] font-bold text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                            {eta.Cubes ? `${parseFloat(eta.Cubes).toFixed(1)} cu ft` : ''}{eta.Units ? ` / ${eta.Units} units` : ''}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            <div className="py-12 text-center text-slate-400 dark:text-slate-500 bg-slate-50/50 dark:bg-slate-900/40 rounded-xl border border-slate-200/60 dark:border-slate-800">
                              <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                              <p className="text-xs font-bold text-slate-600 dark:text-slate-400">No ETA shipment tracking records found for this account.</p>
                              <p className="text-[10px] text-slate-400 mt-1">Matched via vwarmellinieta.Account_Number = {selectedAcct._type === 'consignee' ? selectedAcct.Account_Number : selectedAcct.shipper_account}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

            </div>
          ) : (
            /* Welcome / Empty Detail View Board */
            <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-12 text-center space-y-4 shadow-sm h-full flex flex-col justify-center items-center">
              <div className="w-16 h-16 bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/10 mb-2 animate-pulse">
                <Building className="w-8 h-8" />
              </div>
              <h3 className="text-base font-black text-slate-800 dark:text-slate-150">Logistics Portal Dashboard</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                Select a logistics consignee or shipper account code from the sidebar lookup or history grids to fetch service calendars, active dispatches, and emergency contacts.
              </p>
              
              {/* Features list cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-xl mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 text-left">
                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl space-y-1">
                  <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide">Days of Service</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">Interactive calendar & schedules grouped by regions.</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl space-y-1">
                  <p className="text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wide">DOS Calendar</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">Month-view calendar of service and outbound days per region.</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl space-y-1">
                  <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Contacts File</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">Direct connection to dispatcher roles and contact coordinates.</p>
                </div>
              </div>
            </div>
          )}

        </div>

      </div>

    </div>
  );
}
